import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import type { IBaseAttributes, Model } from '@feathr/rachis';

interface IUseClearUnsavedChangesProps<T extends IBaseAttributes> {
  model: Model<T>;
  promptMessage?: string;
  onClearComplete?: () => void;
  onClearSuccess?: () => void;
  onClearError?: (error: Error) => void;
  onCleanup?: () => void;
}

/**
 * Hook that intercepts navigation attempts when a model has unsaved changes.
 * When navigation is attempted:
 * 1. Shows a confirmation prompt to the user
 * 2. If confirmed, reloads the model to discard changes
 * 3. After cleanup, proceeds with the intended navigation
 *
 * @param model The model to monitor for unsaved changes
 * @param promptMessage Optional custom message for the confirmation prompt
 * @param onClearComplete Optional callback after cleanup and navigation
 * @param onClearSuccess Optional callback after successful cleanup
 * @param onClearError Optional callback if cleanup fails
 * @param onCleanup Optional callback after cleanup and navigation
 */
function useClearUnsavedChanges<T extends IBaseAttributes>({
  model,
  promptMessage: promptMessageProp,
  onClearComplete,
  onClearSuccess,
  onClearError,
  onCleanup,
}: IUseClearUnsavedChangesProps<T>): void {
  const { t } = useTranslation();
  const history = useHistory();

  // Store original history push methods to restore them later
  const originalPushRef = useRef<typeof history.push>(history.push);
  // Store original history replace method to restore them later
  const originalReplaceRef = useRef<typeof history.replace>(history.replace);
  // Track if cleanup is in progress to prevent multiple simultaneous cleanups
  const cleanupInProgressRef = useRef(false);
  // Control whether navigation blocking is active
  const isBlockingRef = useRef(true);

  const promptMessage =
    promptMessageProp ?? t('You have unsaved changes. Are you sure you want to leave this page?');

  // Set up the navigation interceptor
  useEffect(() => {
    // Store original methods if not already stored
    if (!originalPushRef.current) {
      originalPushRef.current = history.push;
    }
    if (!originalReplaceRef.current) {
      originalReplaceRef.current = history.replace;
    }

    // Set up a history blocker that handles both confirmation and cleanup
    const unblock = history.block((location) => {
      // Only block if the model is dirty and we're still blocking
      if (model.isDirty && isBlockingRef.current) {
        // Ask user for confirmation
        const userConfirmed = window.confirm(promptMessage);

        if (userConfirmed) {
          // User confirmed, so we'll do cleanup and then navigate
          if (!model.isPending && model.id && !cleanupInProgressRef.current) {
            cleanupInProgressRef.current = true;

            // Temporarily continue blocking navigation
            isBlockingRef.current = true;

            // Clean up the model
            model
              .reload()
              .then(() => {
                onClearSuccess?.();
              })
              .catch((error) => {
                onClearError?.(error);
              })
              .finally(() => {
                cleanupInProgressRef.current = false;
                isBlockingRef.current = false;

                // Navigate to the intended location after cleanup
                const path = location.pathname + location.search + location.hash;
                originalPushRef.current.call(history, path);

                onClearComplete?.();
              });
          }
        }

        // Always return false to block the current navigation attempt
        // If confirmed, we'll navigate manually after cleanup
        return false;
      }

      // Allow navigation if model isn't dirty and we're not blocking
      return undefined;
    });

    // Cleanup on unmount
    return () => {
      // Remove the navigation block to prevent memory leaks
      unblock();

      onCleanup?.();

      // Restore original methods
      if (history.push !== originalPushRef.current) {
        history.push = originalPushRef.current;
      }
      if (history.replace !== originalReplaceRef.current) {
        history.replace = originalReplaceRef.current;
      }

      // Handle final model cleanup if needed
      // This ensures cleanup happens when component unmounts without navigation,
      // such as when parent unmounts, route changes programmatically, or window closes
      if (model.isDirty && !model.isPending && model.id && !cleanupInProgressRef.current) {
        model.reload();
      }
    };
  }, [history, model, promptMessage]);
}

export default useClearUnsavedChanges;
